import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
  useGetGenesisQuery,
  GenesisRow,
  GenesisSourceKey,
} from "../../services/rtkApi/respectApi";

/**
 * The Season Zero receipt at `/genesis`.
 *
 * Every starting balance was computed from the site's own history, so this page
 * shows the arithmetic: one line per source, summing to the number the user
 * woke up with. It doubles as the guide to how respect behaves from now on,
 * which is the part that matters after the first week.
 *
 * Gated to admin and `writer` while the allocation is still being tuned. It
 * goes public on launch day by dropping the guard in `app_routes`.
 */

const SOURCE_ICONS: Record<GenesisSourceKey, string> = {
  base: "⭐",
  aotd_year: "🎨",
  podium_year: "🏆",
  race_wins: "🏁",
  cap_adjustment: "✂️",
};

function formatAmount(amount: number): string {
  const rounded = Math.round(amount * 100) / 100;
  const body = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
  return rounded > 0 ? `+${body}` : body;
}

function rowDetail(row: GenesisRow): string | null {
  if (row.source === "aotd_year" && row.count !== null) {
    return `${row.count} ${row.count === 1 ? "artwork" : "artworks"}`;
  }
  if (row.source === "podium_year" && row.rank !== null) {
    return `${row.count} that year`;
  }
  if (row.source === "race_wins" && row.count !== null) {
    return `${row.count} ${row.count === 1 ? "win" : "wins"}`;
  }
  return null;
}

const GenesisPage: React.FC = () => {
  const { data, isLoading, isError } = useGetGenesisQuery();
  const genesis = data?.data;
  const viewer = genesis?.viewer ?? null;
  const pending = !genesis || genesis.status === "pending";

  return (
    <PageContainer>
      <Inner>
        <PageHeader>
          <Title>genesis</Title>
          <Subtitle>where your respect came from, line by line</Subtitle>
          <HeaderNav>
            <NavLink to="/profile">your profile</NavLink>
            <NavLink to="/art-of-the-day">art of the day</NavLink>
          </HeaderNav>
        </PageHeader>

        {isLoading && <Muted>loading your allocation…</Muted>}
        {isError && <Muted>could not load the allocation</Muted>}

        {!isLoading && !isError && pending && (
          <EmptyState>
            <strong>season zero has not happened yet.</strong>
            <p>
              nothing has been allocated. when it is, this page fills with your rows and the
              number at the bottom is what you start with. the rest of the guide below is
              already true.
            </p>
          </EmptyState>
        )}

        {!isLoading && !isError && !pending && viewer && (
          <Panel>
            <PanelTitle>@{viewer.username}</PanelTitle>
            <Rows>
              {viewer.rows.map((row, index) => {
                const detail = rowDetail(row);
                return (
                  <Row key={`${row.source}-${row.year ?? index}`}>
                    <RowIcon aria-hidden="true">{SOURCE_ICONS[row.source]}</RowIcon>
                    <RowLabel>
                      {row.label}
                      {detail && <RowDetail>{detail}</RowDetail>}
                    </RowLabel>
                    <RowAmount $negative={row.amount < 0}>{formatAmount(row.amount)}</RowAmount>
                  </Row>
                );
              })}
            </Rows>
            <TotalRow>
              <span>starting balance</span>
              <strong>{viewer.total}</strong>
            </TotalRow>
            {viewer.capped && (
              <CapNote>
                your history came to {viewer.subtotal}. the genesis cap trimmed it, so nobody
                starts the season out of reach.
              </CapNote>
            )}
          </Panel>
        )}

        {!isLoading && !isError && !pending && !viewer && (
          <EmptyState>
            <strong>no genesis rows for your account.</strong>
            <p>
              the allocation ran before you joined. you start at zero and earn from the next
              race.
            </p>
          </EmptyState>
        )}

        {!isLoading && !isError && !pending && (
          <Panel>
            <PanelTitle>everyone</PanelTitle>
            <PanelNote>
              the whole allocation, in the open. anybody it affected can check it.
            </PanelNote>
            <Rows>
              {genesis!.ledger.map((entry) => (
                <Row key={entry.userId} $muted={entry.userId !== viewer?.userId}>
                  <RowIcon aria-hidden="true">{entry.capped ? "✂️" : "•"}</RowIcon>
                  <RowLabel>@{entry.username}</RowLabel>
                  <RowAmount>{entry.total}</RowAmount>
                </Row>
              ))}
              <Row>
                <RowIcon aria-hidden="true">🏛️</RowIcon>
                <RowLabel>
                  the treasury
                  <RowDetail>kept by the sitebot</RowDetail>
                </RowLabel>
                <RowAmount>{genesis!.treasury}</RowAmount>
              </Row>
            </Rows>
            <TotalRow>
              <span>total supply</span>
              <strong>{genesis!.totalSupply}</strong>
            </TotalRow>
          </Panel>
        )}

        <Panel>
          <PanelTitle>how respect works now</PanelTitle>

          <GuideHeading>it only moves</GuideHeading>
          <GuideText>
            there are 450 units of respect and there will never be more. nothing is minted out
            of nowhere and nothing is burned. every unit is held either by a person or by the
            treasury, and the two always add up to 450. so when your balance goes up, it came
            from somewhere specific.
          </GuideText>

          <GuideHeading>what fills the treasury</GuideHeading>
          <GuideList>
            <li>
              <strong>the attendance tax.</strong> 0.5 for a contested day you skipped, when at
              least three other people raced. capped at 2 a week, with a floor of 10 and two
              declared away weeks a season, so a holiday is not a spiral.
            </li>
            <li>
              <strong>playing an aggressive card.</strong> the pointed ones cost 1 or 2.
              commons are free.
            </li>
            <li>
              <strong>fees and lost stakes.</strong> backfires, forfeited wagers, whatever the
              cards put on the table.
            </li>
          </GuideList>

          <GuideHeading>what it pays out</GuideHeading>
          <GuideList>
            <li>
              <strong>2 for winning a contested race</strong> (three or more racers). a solo
              win pays 0.5, the same as showing up.
            </li>
            <li>
              <strong>0.5 for racing and losing.</strong> doubled while you are carrying an
              underdog mark, which is what a mute leaves on you.
            </li>
            <li>
              <strong>4 for a marked win.</strong> win the day somebody muted you and the
              muter pays for it.
            </li>
            <li>
              <strong>1 to 3 for a contribution round</strong> whose idea actually lands, plus
              the occasional tip from the treasurer.
            </li>
          </GuideList>

          <GuideHeading>the treasury and the season</GuideHeading>
          <GuideText>
            the treasury is not a person. it is the pot, and the sitebot keeps it: it narrates
            every movement and gets progressively meaner as the level drops. that level is the
            season clock. it starts high, drains as respect gets handed out, and the season
            ends when it crosses the low-water mark or at week 13, whichever comes first. then
            titles freeze into the record, a quarter of your balance carries over, and the pot
            restocks.
          </GuideText>

          <GuideHeading>why your rows say what they say</GuideHeading>
          <GuideText>
            history was converted, not erased. everyone got a base stake. artworks paid 0.5
            each with a ceiling per year, so a long run counts without making the first year
            unbeatable. the top three authors of each year took a podium bonus, ties going to
            whoever started that year first. contested race wins paid 0.25 each, from
            2026-04-21, which is as far back as race results exist. the whole thing was capped
            per person, and everything left over opened the treasury.
          </GuideText>
        </Panel>
      </Inner>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f0f0f0;
  padding: 20px;

  @media (max-width: 700px) {
    padding: 12px;
  }
`;

const Inner = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding-bottom: 60px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 28px;
  padding: 20px 0;
`;

const Title = styled.h1`
  font-size: 3.5rem;
  color: #222;
  text-transform: uppercase;
  margin: 0;
  text-shadow: 2px 2px 5px #3c3c3cae;

  @media (max-width: 700px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #666;
  margin: 8px 0 0;
  font-style: italic;
`;

const HeaderNav = styled.div`
  display: flex;
  justify-content: center;
  gap: 18px;
  margin-top: 14px;
`;

const NavLink = styled(Link)`
  color: #466a5d;
  font-size: 0.9rem;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Muted = styled.p`
  text-align: center;
  color: #777;
  font-size: 0.95rem;
`;

const Panel = styled.section`
  background: #fff;
  border: 1px solid #e2e2e2;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 18px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const PanelTitle = styled.h2`
  margin: 0 0 4px;
  font-size: 1.2rem;
  color: #222;
`;

const PanelNote = styled.p`
  margin: 0 0 12px;
  font-size: 0.85rem;
  color: #777;
`;

const EmptyState = styled(Panel)`
  text-align: center;
  color: #555;

  p {
    margin: 8px 0 0;
    font-size: 0.92rem;
    line-height: 1.5;
  }
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
`;

const Row = styled.div<{ $muted?: boolean }>`
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px dashed #eee;
  opacity: ${({ $muted }) => ($muted ? 0.7 : 1)};

  &:last-child {
    border-bottom: none;
  }
`;

const RowIcon = styled.span`
  width: 1.4rem;
  flex: 0 0 auto;
  text-align: center;
`;

const RowLabel = styled.span`
  flex: 1 1 auto;
  color: #333;
  font-size: 0.95rem;
`;

const RowDetail = styled.span`
  display: block;
  color: #888;
  font-size: 0.78rem;
`;

const RowAmount = styled.span<{ $negative?: boolean }>`
  flex: 0 0 auto;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: ${({ $negative }) => ($negative ? "#a4553a" : "#2f6a4f")};
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 2px solid #222;
  font-size: 1.05rem;

  strong {
    font-variant-numeric: tabular-nums;
    font-size: 1.4rem;
  }
`;

const CapNote = styled.p`
  margin: 10px 0 0;
  font-size: 0.82rem;
  color: #a4553a;
`;

const GuideHeading = styled.h3`
  margin: 18px 0 6px;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #466a5d;
`;

const GuideText = styled.p`
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.6;
  color: #444;
`;

const GuideList = styled.ul`
  margin: 0;
  padding-left: 18px;
  font-size: 0.92rem;
  line-height: 1.6;
  color: #444;

  li {
    margin-bottom: 6px;
  }
`;

export default GenesisPage;
